import config from 'config'

import { AbstractService, LoggerParameter } from 'shared/services/AbstractService'
import getLogger from 'shared/logger'
import { stringifyArrayFormatRepeat } from '~/shared/url'
import { IS_BROWSER } from '~/app/constants'
import { SSR_PRODUCT_CATALOG_PRICING_TIMEOUT } from '~/shared/constants'
import { createApiClient } from 'shared/services/ApiClient'
import type { GalleryConfigIServiceConfig } from 'shared/types/gallery/config'
import type { PCT } from 'shared/types/vp/pct'

export class ProductCatalogPricingApi extends AbstractService implements PCT.Services.ProductCatalogPricingService.IProductCatalogPricingService {
  apiVersion: number

  constructor({ version, ...rest }: GalleryConfigIServiceConfig, logger: LoggerParameter) {
    super(rest, logger)
    this.apiVersion = version

    if (!IS_BROWSER) {
      this.api = createApiClient(
        {
          ...rest,
          timeout: SSR_PRODUCT_CATALOG_PRICING_TIMEOUT,
        },
        this.constructor.name,
      )
    }
  }

  /**
   * Request Differential Price from PCT's Product Catalog Pricing Service (v4 API).
   *
   * Why this still uses v4 instead of the new v3 Pricing API:
   * This method is only consumed by the built-in Quick View filter pricing
   * (`queryBatchDifferentialPricing`). It will be removed entirely once the new
   * Quick View package (`@vp/quick-view-modal`) fully replaces the legacy
   * built-in Quick View — at that point, no caller of this endpoint will remain.
   *
   * @param market
   * @param productKey
   * @param quantity
   * @param choiceGroups
   * @param pricingContext
   * @param couponCode
   * @param effectiveDateTime
   * @param customerGroups
   */
  public async getDifferentialPrice(
    market: string,
    productKey?: string,
    quantity?: number,
    productVersion?: number,
    choiceGroups?: PCT.Models.ProductCatalogPricingService.ChoiceGroups,
    pricingContext?: string,
    couponCode?: string,
    effectiveDateTime?: string,
    customerGroups?: string[],
    selections?: PCT.Models.ProductCatalogPricingService.ChoiceGroup,
  ): Promise<PCT.Models.ProductCatalogPricingService.DifferentialPricingResult | null> {
    if (!quantity || !productKey || !productVersion) {
      throw new Error('Unable to call getDifferentialPrice. quantity, productKey, and productVersion are required ')
    }

    const choiceGroupsObj = Object.entries(choiceGroups || {}).reduce((newObj, [key, value]) => {
      // If no product options in choice group, we still need to
      // return a non-empty object so choice group price is calculated
      const valueEntries = Object.entries(value)

      if (!valueEntries.length) {
        newObj[`ChoiceGroups[${key}][${''}]`] = ''
      } else {
        valueEntries.forEach(([key2, value2]) => {
          const choiceGroupKey = `ChoiceGroups[${key}][${key2}]`

          newObj[choiceGroupKey] = value2
        })
      }
      return newObj
    }, {} as PCT.Models.ProductCatalogPricingService.ChoiceGroup)

    const selectionsObj = Object.entries(selections || {}).reduce((newObj, [key, value]) => {
      newObj[`Selections[${key}]`] = value

      return newObj
    }, {} as PCT.Models.ProductCatalogPricingService.ChoiceGroup)

    const params = {
      ProductKey: productKey,
      Quantity: quantity,
      ProductVersion: productVersion,
      Market: market,
      MerchantId: 'vistaprint',
      PricingContext: pricingContext,
      CouponCode: couponCode,
      EffectiveDateTime: effectiveDateTime,
      CustomerGroups: customerGroups,
      ...selectionsObj,
      ...choiceGroupsObj,
    }

    const url = `v4/prices/startingAt/differential`

    try {
      const { data } = await this.api.get<PCT.Models.ProductCatalogPricingService.DifferentialPricingResult>(url, {
        params: {
          requestor: config.appName,
          ...params,
        },
        paramsSerializer: stringifyArrayFormatRepeat,
      })

      return data
    } catch {
      return null
    }
  }

  /**
   * Request Pricing for all quantities from PCT's Product Catalog Pricing Service (v4 API).
   *
   * Why this still uses v4 instead of the new v3 Pricing API:
   * This method powers the `trackProductOptionsSelected` analytics event
   * (`queryBatchEstimatedPricing`), which relies on the `breakdown` property in
   * the v4 response to identify which specific productOption changed between
   * initial and selected options (used to record per-property "upsell" in Segment).
   * The v3 Pricing API does not provide a `breakdown` equivalent, and adding one
   * would require a discovery effort on the Pricing team's side with no timeline.
   * Therefore this endpoint cannot be removed or migrated until v3 gains breakdown
   * support or the analytics requirement changes.
   *
   * @param market
   * @param productKey
   * @param quantity
   * @param choiceGroups
   * @param pricingContext
   * @param couponCode
   * @param effectiveDateTime
   * @param customerGroups
   */
  public async getQuantityPrices(
    market: string,
    productKey?: string,
    quantities?: number[],
    productVersion?: number,
    pricingContext?: string,
    selections?: PCT.Models.ProductCatalogPricingService.ChoiceGroup,
  ): Promise<PCT.Models.ProductCatalogPricingService.QuantitiesPricingResult | null> {
    if (!quantities || !productKey || !productVersion) {
      throw new Error('Unable to call getQuantityPrices. quantities, productKey, and productVersion are required ')
    }

    const selectionsObj = Object.entries(selections || {}).reduce((newObj, [key, value]) => {
      newObj[`Selections[${key}]`] = value

      return newObj
    }, {} as PCT.Models.ProductCatalogPricingService.ChoiceGroup)

    const params = {
      ProductKey: productKey,
      ProductVersion: productVersion,
      Market: market,
      MerchantId: 'vistaprint',
      PricingContext: pricingContext,
      OptionalPriceComponents: 'UnitPrice',
      Quantities: quantities,
      ...selectionsObj,
    }

    const url = `v4/prices/startingAt/estimated`

    try {
      const { data } = await this.api.get<PCT.Models.ProductCatalogPricingService.QuantitiesPricingResult>(url, {
        params: {
          requestor: config.appName,
          ...params,
        },
        paramsSerializer: stringifyArrayFormatRepeat,
      })

      return data
    } catch {
      return null
    }
  }
}

export const ProductCatalogPricingService = new ProductCatalogPricingApi(config.services.productCatalogPricingService, getLogger)
